import React from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  LogOut,
  Menu,
  X,
  Shield,
  FileText,
  Quote,
  MessageSquare,
  GraduationCap,
  Newspaper,
  DollarSign,
  Activity,
  BarChart3
} from 'lucide-react';
import { useAuth } from '../../App';

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: Users, label: 'Clients', path: '/admin/clients' },
    { icon: GraduationCap, label: 'Coaches', path: '/admin/coaches' },
    { icon: DollarSign, label: 'Coach Revenue', path: '/admin/coach-subscriptions' },
    { icon: Activity, label: 'TPI Screenings', path: '/admin/tpi' },
    { icon: BarChart3, label: 'A³ Results', path: '/admin/a3-results' },
    { icon: Newspaper, label: 'Email Log', path: '/admin/email-log' },
    { icon: MessageSquare, label: 'Messages', path: '/admin/messages' },
    { icon: Newspaper, label: 'Newsletter', path: '/admin/newsletter' },
    { icon: FileText, label: 'Blog', path: '/admin/blog' },
    { icon: Quote, label: 'Testimonials', path: '/admin/testimonials' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex" data-testid="admin-layout">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-[#1A1A1A] text-white fixed h-full" data-testid="admin-sidebar">
        {/* Logo */}
        <div className="p-6 border-b border-white/10 flex-shrink-0">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_golf-experience-hub/artifacts/amevwgso_logo-for-web%20copy.jpg" 
              alt="Quiet Mind Golf" 
              className="h-10 w-auto bg-white rounded-full p-1"
            />
            <div>
              <span className="font-['Playfair_Display'] text-lg font-medium block">Quiet Mind</span>
              <span className="text-xs text-white/60 flex items-center gap-1">
                <Shield className="w-3 h-3" />
                Admin Portal
              </span>
            </div>
          </Link>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 py-6 px-4 overflow-y-auto">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === '/admin'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                      isActive
                        ? 'bg-[#BC562B] text-white'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`
                  }
                  data-testid={`admin-nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
          
          {/* Switch to Client View */}
          <div className="mt-8 pt-8 border-t border-white/10">
            <Link
              to="/dashboard"
              className="flex items-center gap-3 px-4 py-3 text-white/50 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
            >
              <LayoutDashboard className="w-5 h-5" />
              <span className="text-sm">Switch to Client View</span>
            </Link>
          </div>
        </nav>

        {/* User & Logout - Fixed at bottom */}
        <div className="p-4 border-t border-white/10 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="px-2">
              <p className="text-white font-medium truncate text-sm">{user?.full_name}</p>
              <p className="text-white/60 text-xs truncate">{user?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-white/70 hover:bg-white/10 hover:text-white rounded-sm transition-colors"
              data-testid="admin-logout-btn"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium text-sm">Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#1A1A1A] text-white h-16 flex items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_golf-experience-hub/artifacts/amevwgso_logo-for-web%20copy.jpg" 
            alt="Quiet Mind Golf" 
            className="h-8 w-auto bg-white rounded-full p-0.5"
          />
          <div>
            <span className="font-['Playfair_Display'] font-medium block text-sm">Quiet Mind</span>
            <span className="text-xs text-white/60">Admin</span>
          </div>
        </Link>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2"
          data-testid="admin-mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)}></div>
          <aside className="absolute top-16 left-0 bottom-0 w-64 bg-[#1A1A1A] text-white flex flex-col">
            <nav className="flex-1 py-6 px-4 overflow-y-auto">
              <ul className="space-y-1">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.path === '/admin'}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                          isActive
                            ? 'bg-[#BC562B] text-white'
                            : 'text-white/70 hover:bg-white/10 hover:text-white'
                        }`
                      }
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
              
              {/* Switch to Client View */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <Link
                  to="/dashboard"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-white/50 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
                >
                  <LayoutDashboard className="w-5 h-5" />
                  <span className="text-sm">Switch to Client View</span>
                </Link>
              </div>
            </nav>
            
            {/* User & Logout - Fixed at bottom of mobile sidebar */}
            <div className="p-4 border-t border-white/10 flex-shrink-0">
              <div className="px-2 mb-3">
                <p className="text-white font-medium truncate text-sm">{user?.full_name}</p>
                <p className="text-white/60 text-xs truncate">{user?.email}</p>
              </div>
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center gap-2 px-4 py-3 text-white/70 hover:bg-white/10 hover:text-white rounded-sm transition-colors"
                data-testid="admin-mobile-logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium text-sm">Sign Out</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 md:p-8 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
